import { useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useCanvasSize } from '../hooks/useCanvasSize';
import { setupInteractions } from './interactionHandler';
import { createUniverseRenderer } from './universeRenderer';
import { buildUniverseLayout } from './universeGraphBuilder';
import { clamp } from '../utils/mathUtils';

// Detail panel sidebar width (matches panel.css). On viewports wider than
// PANEL_BREAKPOINT the panel renders as a right sidebar, so the focused node
// is centered in the visible canvas to the left of it; below that width the
// panel becomes a top sheet that doesn't steal horizontal space.
const DETAIL_PANEL_WIDTH = 320;
const PANEL_BREAKPOINT = 480;

/**
 * Single continuous canvas for the My Universe view.
 * Camera zoom controls LOD transitions automatically.
 */

function computeFitTransform(items, viewWidth, viewHeight, padding = 60) {
  if (!items || items.length === 0) return { x: 0, y: 0, scale: 1 };

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const item of items) {
    if (item.x == null || item.y == null) continue;
    const r = item.radius || item.size || 5;
    minX = Math.min(minX, item.x - r);
    minY = Math.min(minY, item.y - r);
    maxX = Math.max(maxX, item.x + r);
    maxY = Math.max(maxY, item.y + r);
  }

  if (!isFinite(minX)) return { x: 0, y: 0, scale: 1 };

  const graphWidth = maxX - minX;
  const graphHeight = maxY - minY;
  if (graphWidth === 0 || graphHeight === 0) return { x: 0, y: 0, scale: 1 };

  const scaleX = (viewWidth - padding * 2) / graphWidth;
  const scaleY = (viewHeight - padding * 2) / graphHeight;
  const scale = clamp(Math.min(scaleX, scaleY), 0.05, 3);

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const x = viewWidth / 2 - cx * scale;
  const y = viewHeight / 2 - cy * scale;

  return { x, y, scale };
}

function animateTransform(stateRef, target, duration = 500) {
  const start = { ...stateRef.current.transform };
  const startTime = performance.now();

  function step(now) {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / duration, 1);
    const ease = 1 - Math.pow(1 - t, 3);

    stateRef.current.transform = {
      x: start.x + (target.x - start.x) * ease,
      y: start.y + (target.y - start.y) * ease,
      scale: start.scale + (target.scale - start.scale) * ease,
    };

    if (t < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

const UniverseCanvas = forwardRef(function UniverseCanvas(
  { universeData, favorites, discoveredArtists, dislikes, onSelectNode, onHoverNode },
  ref
) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  const layoutRef = useRef(null);
  const bigBangShownRef = useRef(false);

  const stateRef = useRef({
    clusterMetas: [],
    superclusterMetas: [],
    allNodes: [],
    allLinks: [],
    worldBounds: { minX: 0, minY: 0, maxX: 0, maxY: 0 },
    transform: { x: 0, y: 0, scale: 1 },
    hoveredNode: null,
    selectedNode: null,
    favoriteNames: new Set(),
    dislikeNames: new Set(),
    discoveredNames: new Set(),
    bigBangStart: null,
  });

  const rendererRef = useRef(null);
  const cleanupRef = useRef(null);

  const size = useCanvasSize(containerRef);

  // Build layout when universeData changes
  useEffect(() => {
    if (!universeData) return;

    const layout = buildUniverseLayout(universeData);
    layoutRef.current = layout;

    stateRef.current.clusterMetas = layout.clusterMetas;
    stateRef.current.superclusterMetas = layout.superclusterMetas || [];
    stateRef.current.allNodes = layout.allNodes;
    stateRef.current.allLinks = layout.allLinks;
    stateRef.current.worldBounds = layout.worldBounds;
    stateRef.current.hoveredNode = null;
    stateRef.current.selectedNode = null;
  }, [universeData]);

  // Sync favorite/discovered/dislike names into renderer state for real-time ring effects
  useEffect(() => {
    stateRef.current.favoriteNames = new Set(
      (favorites || []).map((f) => f.artistName)
    );
  }, [favorites]);

  useEffect(() => {
    stateRef.current.discoveredNames = new Set(
      (discoveredArtists || []).map((d) => d.artistName)
    );
  }, [discoveredArtists]);

  useEffect(() => {
    stateRef.current.dislikeNames = new Set(
      (dislikes || []).map((d) => d.artistName)
    );
  }, [dislikes]);

  const fitAll = useCallback((animate = true) => {
    const layout = layoutRef.current;
    if (!layout || !layout.clusterMetas.length || size.width === 0) return;

    // Use superclusters as fit targets if available, otherwise clusters
    const hasSC = layout.superclusterMetas && layout.superclusterMetas.length > 1;
    const fitTargets = hasSC
      ? layout.superclusterMetas.map((sc) => ({
          x: sc.cx, y: sc.cy, radius: sc.visualRadius,
        }))
      : layout.clusterMetas.map((cm) => ({
          x: cm.cx, y: cm.cy, radius: cm.visualRadius,
        }));

    const items = fitTargets;
    const target = computeFitTransform(items, size.width, size.height, 40);

    // Zoom in 40% closer than pure fit so clusters appear larger and
    // more visually striking at the overview level.  Use a gentler
    // boost for supercluster views so the full extent is visible.
    const zoomBoost = hasSC ? 1.15 : 1.4;
    const cx = size.width / 2;
    const cy = size.height / 2;
    target.scale = clamp(target.scale * zoomBoost, 0.05, 3);
    target.x = cx - (cx - target.x) * zoomBoost;
    target.y = cy - (cy - target.y) * zoomBoost;

    if (animate) {
      animateTransform(stateRef, target, 500);
    } else {
      stateRef.current.transform = target;
    }
  }, [size.width, size.height]);

  const zoomToCluster = useCallback((clusterId) => {
    const layout = layoutRef.current;
    if (!layout) return;
    const cm = layout.clusterMetas.find((c) => c.index === clusterId);
    if (!cm || size.width === 0) return;

    const targetScale = Math.min(
      (size.width * 0.6) / (cm.visualRadius * 2),
      (size.height * 0.6) / (cm.visualRadius * 2),
      2.5
    );

    const target = {
      x: size.width / 2 - cm.cx * targetScale,
      y: size.height / 2 - cm.cy * targetScale,
      scale: targetScale,
    };

    animateTransform(stateRef, target, 700);
  }, [size.width, size.height]);

  const zoomToSupercluster = useCallback((superclusterId) => {
    const layout = layoutRef.current;
    if (!layout || !layout.superclusterMetas) return;
    const sc = layout.superclusterMetas.find((s) => s.id === superclusterId);
    if (!sc || size.width === 0) return;

    const targetScale = Math.max(
      Math.min(
        (size.width * 0.6) / (sc.visualRadius * 2),
        (size.height * 0.6) / (sc.visualRadius * 2),
        1.0
      ),
      0.15
    );

    const target = {
      x: size.width / 2 - sc.cx * targetScale,
      y: size.height / 2 - sc.cy * targetScale,
      scale: targetScale,
    };

    animateTransform(stateRef, target, 900);
  }, [size.width, size.height]);

  const zoomBy = useCallback((factor) => {
    const t = stateRef.current.transform;
    const cx = size.width / 2;
    const cy = size.height / 2;
    const newScale = clamp(t.scale * factor, 0.05, 8);
    const ratio = newScale / t.scale;
    animateTransform(stateRef, {
      x: cx - (cx - t.x) * ratio,
      y: cy - (cy - t.y) * ratio,
      scale: newScale,
    }, 200);
  }, [size.width, size.height]);

  const zoomToNode = useCallback((node) => {
    if (!node || size.width === 0) return;
    const targetScale = clamp(1.5, 0.05, 8);
    // Offset center so the node sits in the visible area left of the sidebar
    const panelOffset = size.width > PANEL_BREAKPOINT ? DETAIL_PANEL_WIDTH / 2 : 0;
    const target = {
      x: size.width / 2 - panelOffset - node.x * targetScale,
      y: size.height / 2 - node.y * targetScale,
      scale: targetScale,
    };
    stateRef.current.selectedNode = node;
    if (onSelectNode) onSelectNode(node);
    animateTransform(stateRef, target, 700);
  }, [size.width, size.height, onSelectNode]);

  const getNodes = useCallback(() => {
    return stateRef.current.allNodes || [];
  }, []);

  const getLinks = useCallback(() => {
    return stateRef.current.allLinks || [];
  }, []);

  // Capture canvas as a PNG data URL with optional watermark
  const captureImage = useCallback((options = {}) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    if (options.watermark) {
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      ctx.save();
      ctx.font = `600 ${14 * dpr}px 'Space Grotesk', sans-serif`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.textAlign = 'right';
      ctx.fillText('Stellar', canvas.width - 16 * dpr, canvas.height - 12 * dpr);
      ctx.restore();
    }

    return canvas.toDataURL('image/png');
  }, []);

  const followNode = useCallback((node) => {
    if (!node || node.x == null || node.y == null || size.width === 0 || size.height === 0) return;

    // Match by ID to get the renderer's own node reference so the
    // identity comparison (node === selectedNode) works for highlighting.
    const nodes = stateRef.current.allNodes || [];
    const match = nodes.find((n) => n.id === node.id);
    stateRef.current.selectedNode = match || node;

    // Gather the target node + direct neighbors for context framing
    const links = stateRef.current.allLinks || [];
    const neighbors = [node];
    for (const link of links) {
      const src = link.source;
      const tgt = link.target;
      if (src === match || src?.id === node.id) {
        if (tgt && tgt.x != null) neighbors.push(tgt);
      } else if (tgt === match || tgt?.id === node.id) {
        if (src && src.x != null) neighbors.push(src);
      }
    }

    // Compute scale that fits the node + neighbors, then bias tighter
    // so the target node feels prominent, not lost among neighbors
    const fit = computeFitTransform(neighbors, size.width, size.height, 80);
    const scale = clamp(fit.scale * 1.15, 1.1, 1.8);

    // Offset center so the node sits in the visible area left of the sidebar
    const panelOffset = size.width > PANEL_BREAKPOINT ? DETAIL_PANEL_WIDTH / 2 : 0;

    // Center on the target node (not the neighbor centroid)
    const target = {
      x: size.width / 2 - panelOffset - node.x * scale,
      y: size.height / 2 - node.y * scale,
      scale,
    };

    // Use a gentle ease-in-out curve for a smooth glide between nodes
    const start = { ...stateRef.current.transform };
    const startTime = performance.now();
    const duration = 1400;

    function step(now) {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      // Ease-in-out sine: smooth acceleration and deceleration
      const ease = -(Math.cos(Math.PI * t) - 1) / 2;

      stateRef.current.transform = {
        x: start.x + (target.x - start.x) * ease,
        y: start.y + (target.y - start.y) * ease,
        scale: start.scale + (target.scale - start.scale) * ease,
      };

      if (t < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }, [size.width, size.height]);

  useImperativeHandle(ref, () => ({
    resetView: () => fitAll(true),
    zoomToCluster,
    zoomToSupercluster,
    zoomBy,
    zoomToNode,
    followNode,
    getNodes,
    getLinks,
    captureImage,
  }), [fitAll, zoomToCluster, zoomToSupercluster, zoomBy, zoomToNode, followNode, getNodes, getLinks, captureImage]);

  // Set up canvas, renderer, and interactions
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layoutRef.current || size.width === 0 || size.height === 0) return;
    if (!layoutRef.current.clusterMetas.length) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size.width * dpr;
    canvas.height = size.height * dpr;
    canvas.style.width = size.width + 'px';
    canvas.style.height = size.height + 'px';

    fitAll(false);

    if (!bigBangShownRef.current) {
      stateRef.current.bigBangStart = performance.now();
      bigBangShownRef.current = true;
    }

    if (rendererRef.current) rendererRef.current.stop();
    const renderer = createUniverseRenderer(canvas, () => stateRef.current);
    rendererRef.current = renderer;
    renderer.start();

    if (cleanupRef.current) cleanupRef.current();

    const cleanup = setupInteractions(
      canvas,
      () => {
        const scale = stateRef.current.transform.scale;
        const scMetas = stateRef.current.superclusterMetas || [];

        // At extreme zoom-out with superclusters, return supercluster centers
        if (scale < 0.12 && scMetas.length > 1) {
          return scMetas.map((sc) => ({
            x: sc.cx,
            y: sc.cy,
            radius: sc.visualRadius,
            _isSuperclusterCenter: true,
            _superclusterId: sc.id,
            name: sc.label,
          }));
        }

        if (scale < 0.4) {
          return (stateRef.current.clusterMetas || []).map((cm) => ({
            x: cm.cx,
            y: cm.cy,
            radius: cm.visualRadius,
            _isClusterCenter: true,
            _clusterIndex: cm.index,
            name: cm.label,
          }));
        }
        return stateRef.current.allNodes || [];
      },
      () => stateRef.current.transform,
      {
        onHover: (node) => {
          if (node && (node._isClusterCenter || node._isSuperclusterCenter)) {
            stateRef.current.hoveredNode = null;
          } else {
            stateRef.current.hoveredNode = node;
          }
          if (onHoverNode) onHoverNode(node);
        },
        onClick: (node) => {
          if (node && node._isSuperclusterCenter) {
            zoomToSupercluster(node._superclusterId);
            return;
          }
          if (node && node._isClusterCenter) {
            zoomToCluster(node._clusterIndex);
            return;
          }
          stateRef.current.selectedNode = node || null;
          if (onSelectNode) onSelectNode(node || null);
        },
        onPan: (dx, dy) => {
          const t = stateRef.current.transform;
          stateRef.current.transform = { ...t, x: t.x + dx, y: t.y + dy };
        },
        onZoom: (delta, cx, cy) => {
          const t = stateRef.current.transform;
          const newScale = clamp(t.scale * delta, 0.05, 8);
          const ratio = newScale / t.scale;
          stateRef.current.transform = {
            x: cx - (cx - t.x) * ratio,
            y: cy - (cy - t.y) * ratio,
            scale: newScale,
          };
        },
      }
    );
    cleanupRef.current = cleanup;

    return () => {
      renderer.stop();
      rendererRef.current = null;
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [universeData, size.width, size.height, fitAll, zoomToCluster, zoomToSupercluster, onSelectNode, onHoverNode]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      <canvas
        ref={canvasRef}
        style={{ display: 'block', position: 'absolute', top: 0, left: 0, cursor: 'grab' }}
      />
    </div>
  );
});

export default UniverseCanvas;
